import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import api from './api';
import { useAuth } from './auth';

const CycleContext = createContext(null);

export function CycleProvider({ children }) {
  const { user } = useAuth();
  const [units, setUnits] = useState([]);
  const [cycles, setCycles] = useState([]);
  const [currentUnit, setCurrentUnit] = useState(null);
  const [currentCycle, setCurrentCycle] = useState(null);
  const [loading, setLoading] = useState(false);

  const refresh = useCallback(async () => {
    if (!user) return;
    setLoading(true);
    try {
      const [uRes, cRes] = await Promise.all([api.get('/units'), api.get('/cycles')]);
      const activeUnits = (uRes.data || []).filter((u) => u.is_active);
      // Restrict for HODs without all_units
      let visibleUnits = activeUnits;
      if (user.role === 'hod' && !user.all_units) {
        const allowed = new Set((user.unit_ids || []).map(String));
        visibleUnits = activeUnits.filter((u) => allowed.has(u.id));
      }
      setUnits(visibleUnits);
      setCycles(cRes.data || []);
      // pick saved or first
      const savedUnitId = localStorage.getItem('mrm_unit_id');
      let pickedUnit = visibleUnits.find((u) => u.id === savedUnitId) || visibleUnits[0] || null;
      setCurrentUnit(pickedUnit);
      if (pickedUnit) localStorage.setItem('mrm_unit_id', pickedUnit.id);
      // pick cycle for this unit
      pickCycleForUnit(cRes.data || [], pickedUnit);
    } catch (e) {
      console.error('cycle refresh', e);
    } finally { setLoading(false); }
  // eslint-disable-next-line
  }, [user]);

  const pickCycleForUnit = (allCycles, unit) => {
    if (!unit) { setCurrentCycle(null); return; }
    const unitCycles = allCycles.filter((c) => c.unit_id === unit.id);
    const savedId = localStorage.getItem('mrm_cycle_id_' + unit.id);
    let pick = null;
    if (savedId) pick = unitCycles.find((c) => c.id === savedId);
    // Default reporting period = the last fully completed month (Oct 2026 -> Sep 2026), never hardcoded.
    if (!pick) {
      const now = new Date();
      const lcYear = now.getMonth() === 0 ? now.getFullYear() - 1 : now.getFullYear();
      const lcMonth = now.getMonth() === 0 ? 12 : now.getMonth();
      pick = unitCycles.find((c) => Number(c.year) === lcYear && Number(c.month) === lcMonth);
    }
    if (!pick) pick = unitCycles.find((c) => c.status === 'open' || c.status === 'data_entry');
    if (!pick && unitCycles.length > 0) pick = unitCycles[0];
    setCurrentCycle(pick || null);
    if (pick) localStorage.setItem('mrm_cycle_id_' + unit.id, pick.id);
  };

  useEffect(() => { refresh(); }, [refresh]);

  const selectUnit = (unit) => {
    setCurrentUnit(unit);
    if (unit?.id) localStorage.setItem('mrm_unit_id', unit.id);
    pickCycleForUnit(cycles, unit);
  };

  const selectCycle = (cycle) => {
    setCurrentCycle(cycle);
    if (cycle?.id && cycle.unit_id) localStorage.setItem('mrm_cycle_id_' + cycle.unit_id, cycle.id);
  };

  return (
    <CycleContext.Provider value={{ units, cycles, currentUnit, currentCycle, loading, refresh, selectUnit, selectCycle }}>
      {children}
    </CycleContext.Provider>
  );
}

export const useCycle = () => useContext(CycleContext);
